import React, { FC, memo, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import clsx from 'clsx';
import './GradientEnergyWaveform.scss';
import { HeatmapDataType } from '@demind-inc/core';
import { getCssVariable } from '../../../utils';
import { PeakDipDataPoint } from '../../Onboarding';
import { Typography } from '@mui/material';

interface GradientEnergyWaveformProps {
  data: HeatmapDataType[];
  className?: string;
  height?: number;
  width?: number | string;
  showGrid?: boolean;
  useGradient?: boolean;
  peakDipDataPoint?: PeakDipDataPoint;
  /** Solid smooth line instead of the default dashed stroke. */
  smooth?: boolean;
  /** Marks the data point closest to "now" with a dot on the curve. */
  showCurrentIndicator?: boolean;
}

interface WaveformData {
  time: number;
  value: number;
}

const purpleLight = getCssVariable('--color-purple-light') || '#ebe5fd';
const grayDark = getCssVariable('--color-gray-dark') || '#7c7c7c';

const GradientEnergyWaveform: FC<GradientEnergyWaveformProps> = memo(
  ({
    data,
    className,
    width = '100%',
    height = 200,
    showGrid = true,
    useGradient = false,
    peakDipDataPoint,
    smooth = false,
    showCurrentIndicator = false,
  }) => {
    const formatXAxis = (timestamp: number) => (showGrid ? dayjs(timestamp).format('h:mma') : '');
    const formatYAxis = (value: number) => (showGrid ? `${value}` : '');

    const formattedData = useMemo(() => {
      return data.map(
        (d) => ({ value: d.value * 100, time: dayjs(d.y).valueOf() }) as WaveformData
      );
    }, [data]);

    const xAxisTicks = useMemo(() => {
      const ticks = [];
      if (formattedData.length > 0) {
        ticks.push(formattedData[0].time); // start
        if (peakDipDataPoint?.morningDipData?.y) {
          ticks.push(dayjs(peakDipDataPoint.morningDipData.y).valueOf());
        }
        if (peakDipDataPoint?.morningPeakData?.y) {
          ticks.push(dayjs(peakDipDataPoint.morningPeakData.y).valueOf());
        }
        if (peakDipDataPoint?.eveningPeakData?.y) {
          ticks.push(dayjs(peakDipDataPoint.eveningPeakData.y).valueOf());
        }
        if (formattedData[formattedData.length - 1].time !== formattedData[0].time)
          ticks.push(formattedData[formattedData.length - 1].time); // end
      }
      // Remove duplicates and sort
      return Array.from(new Set(ticks)).sort((a, b) => a - b);
    }, [formattedData, peakDipDataPoint]);

    // Closest data point to "now" — used by showCurrentIndicator.
    const currentTime = useMemo(() => {
      if (!showCurrentIndicator || formattedData.length === 0) return null;
      const now = dayjs().valueOf();
      return formattedData.reduce(
        (closest, point) => (Math.abs(point.time - now) < Math.abs(closest - now) ? point.time : closest),
        formattedData[0].time
      );
    }, [formattedData, showCurrentIndicator]);

    // Y axis ticks: 0, 100, and peakDipDataPoint values
    const yAxisTicks = useMemo(() => {
      const ticks = [0, 100];
      if (peakDipDataPoint?.morningPeakData?.value !== undefined) {
        ticks.push(Math.round(peakDipDataPoint.morningPeakData.value * 100));
      }
      if (peakDipDataPoint?.eveningPeakData?.value !== undefined) {
        ticks.push(Math.round(peakDipDataPoint.eveningPeakData.value * 100));
      }
      if (peakDipDataPoint?.morningDipData?.value !== undefined) {
        ticks.push(Math.round(peakDipDataPoint.morningDipData.value * 100));
      }
      // Remove duplicates and sort
      return Array.from(new Set(ticks)).sort((a, b) => a - b);
    }, [peakDipDataPoint]);

    // When the grid/axes are hidden (e.g. rich-context energy card),
    // drop chart padding so the stroke can span the full container width.
    const chartMargin = showGrid
      ? { top: 5, right: 20, bottom: 15, left: 30 }
      : { top: 8, right: 4, bottom: 8, left: 4 };

    return (
      <div className={clsx('gradient-energy-waveform', className)}>
        <ResponsiveContainer width={width} height={height}>
          <LineChart data={formattedData} margin={chartMargin}>
            {showGrid && (
              <CartesianGrid
                stroke={purpleLight}
                vertical={(props) => {
                  const { x1, x2, y1, y2, x } = props;
                  return (
                    <line
                      x1={x1}
                      x2={x2}
                      y1={y1}
                      y2={y2}
                      stroke={purpleLight}
                      strokeWidth={2}
                      strokeDasharray="10 10"
                      strokeLinecap="round"
                    />
                  );
                }}
                horizontal={(props) => {
                  const { x1, x2, y1, y2, y } = props;
                  if (y1 === 5) {
                    // Remove the solid line when score is 100
                    return null;
                  }
                  return (
                    <line
                      x1={x1}
                      x2={x2}
                      y1={y1}
                      y2={y2}
                      stroke={purpleLight}
                      strokeWidth={2}
                      strokeDasharray="10 10"
                      strokeLinecap="round"
                    />
                  );
                }}
              />
            )}
            <XAxis
              dataKey="time"
              tickFormatter={formatXAxis}
              ticks={xAxisTicks}
              type="number"
              style={{ fontSize: '12px', fill: grayDark }}
              domain={[
                dayjs(formattedData[0]?.time).valueOf(),
                dayjs(formattedData[formattedData.length - 1]?.time).valueOf(),
              ]}
              tickLine={false}
              axisLine={false}
              dy={showGrid ? 8 : 0}
              height={showGrid ? undefined : 0}
            />
            <YAxis
              type="number"
              tickFormatter={formatYAxis}
              domain={[0, 100]}
              ticks={yAxisTicks}
              axisLine={false}
              tickLine={false}
              interval={0}
              tickMargin={showGrid ? 32 : 0}
              width={showGrid ? 35 : 0}
              style={{ fontSize: '12px', fill: grayDark }}
              allowDataOverflow={true}
            />
            {useGradient && (
              <defs>
                <linearGradient id="strokeGradient" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#ef4444" />
                  <stop offset="50%" stopColor="#eab308" />
                  <stop offset="100%" stopColor="#22c55e" />
                </linearGradient>
              </defs>
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={useGradient ? 'url(#strokeGradient)' : '#680ddb'}
              strokeWidth={useGradient ? 10 : 2}
              strokeDasharray={smooth ? undefined : '10 10'}
              strokeLinecap="round"
              dot={(props: any) => {
                const { cx, cy, payload, key } = props;
                const pointTime = payload.time;

                if (peakDipDataPoint) {
                  const morningPeakTime = peakDipDataPoint.morningPeakData
                    ? dayjs(peakDipDataPoint.morningPeakData.y).valueOf()
                    : null;
                  const eveningPeakTime = peakDipDataPoint.eveningPeakData
                    ? dayjs(peakDipDataPoint.eveningPeakData.y).valueOf()
                    : null;
                  const morningDipTime = peakDipDataPoint.morningDipData
                    ? dayjs(peakDipDataPoint.morningDipData.y).valueOf()
                    : null;

                  const isPeakOrDip =
                    pointTime === morningPeakTime ||
                    pointTime === eveningPeakTime ||
                    pointTime === morningDipTime;

                  if (isPeakOrDip) {
                    return <circle key={key} cx={cx} cy={cy} r={6.5} fill="#680ddb" stroke="#680ddb" />;
                  }
                }

                if (showCurrentIndicator && pointTime === currentTime) {
                  return (
                    <circle
                      key={key}
                      cx={cx}
                      cy={cy}
                      r={4.5}
                      fill="#ffffff"
                      stroke="#680ddb"
                      strokeWidth={2.5}
                    />
                  );
                }

                return null;
              }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
);

export default GradientEnergyWaveform;
